import { useMemo } from "react";
import {
  useNavigate,
  useLocation,
  useParams as useRouterParams,
  useSearchParams as useRouterSearchParams,
  Navigate,
} from "react-router-dom";
import { useRefresh } from "@frontend/lib/refresh";

export { Navigate };

/** Drop-in for next/navigation `usePathname`. */
export function usePathname(): string {
  return useLocation().pathname;
}

/** Drop-in for next/navigation `useSearchParams` (returns URLSearchParams + setter). */
export function useSearchParams(): [
  URLSearchParams,
  (next: URLSearchParams | Record<string, string>, opts?: { replace?: boolean }) => void,
] {
  const [params, setParams] = useRouterSearchParams();
  const set = (
    next: URLSearchParams | Record<string, string>,
    opts?: { replace?: boolean },
  ) => {
    if (next instanceof URLSearchParams) {
      setParams(next, { replace: opts?.replace });
      return;
    }
    setParams(next, { replace: opts?.replace });
  };
  return [params, set];
}

/** Drop-in for next/navigation `useParams`. */
export function useParams<T extends Record<string, string | undefined> = Record<string, string>>() {
  return useRouterParams() as T;
}

type AppRouter = {
  push: (href: string) => void;
  replace: (href: string) => void;
  back: () => void;
  refresh: () => void;
  prefetch: (href: string) => void;
};

/** Drop-in for next/navigation `useRouter` (includes SPA `refresh`). */
export function useRouter(): AppRouter {
  const navigate = useNavigate();
  const { refresh } = useRefresh();
  // Stable identity — poll effects must not restart every parent render.
  return useMemo(
    () => ({
      push: (href: string) => navigate(href),
      replace: (href: string) => navigate(href, { replace: true }),
      back: () => navigate(-1),
      refresh,
      prefetch: () => {},
    }),
    [navigate, refresh],
  );
}

/** Client-side redirect helper (replaces next/navigation `redirect` in layouts). */
export function redirect(href: string): never {
  if (typeof window !== "undefined") {
    window.location.replace(href);
  }
  throw new Error(`Redirect to ${href}`);
}

export function notFound(): never {
  throw new Response("Not Found", { status: 404 });
}
